import cn from 'classnames';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { CartographySelectorModal } from 'sonata-platform-components';
import {
  ButtonUI,
  Card,
  Checkbox,
  CustomThemeProvider,
  InputSearch,
  LoadingSkeleton,
  ModalUI,
  PopoverMenuButton,
  Switch,
  Tag,
  TaptapList,
  usePortal,
  useQueryFetch
} from 'sonata-react-components';
import { formatPercentage } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';
import { isEqual } from 'taptap-tools/utils';

import { PoiStoreTable } from '@/components/driveToStore/common/PoiStoreTable/PoiStoreTable';
import SelectionToolbar from '@/components/driveToStore/common/SelectionToolbar/SelectionToolbar';
import { ViewToggle } from '@/components/ViewToggle/ViewToggle';
import { getPoiImageUrl } from '@/helpers/common/poiAttribution.helper';
import { useNavigate } from '@/router';
import { getPoiListWithUplift } from '@/services/footfall.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { getDriveToStoreReportUrl } from '../common/hooks/useDriveToStoreNavigation';
import { DEFAULT_METRIC_SORT, MetricSortPanel } from './MetricSortPanel/MetricSortPanel';
import { NoUpliftConfirmPrompt } from './NoUpliftConfirmPrompt';
import { PoiFiltersSidebar } from './PoiFiltersSidebar/PoiFiltersSidebar';
import { SelectionSummaryPanel } from './SelectionSummaryPanel/SelectionSummaryPanel';

import type { ReactNode } from 'react';
import type { AdminBoundariesChangeItem, AdminBoundariesItemInput } from 'sonata-platform-components';
import type { MetricFilters, MetricsUnitMode, PoiWithUplift } from '@/services/footfall.service';
import type { MetricSort } from './MetricSortPanel/MetricSortPanel';
import type { BrandOption, MetricBounds, MetricKey } from './PoiFiltersSidebar/PoiFiltersSidebar';

import './poiSelectionModal.scss';

type StepEntry = {
  component: ReactNode;
  description: string;
  title: string;
};

type ModalHandler = {
  closeModal: () => void;
  openModal: () => void;
};

type ReportType = 'single' | 'aggregated';

type Props = {
  entityType: string;
  entityId: string;
  entityCountries: string[];
  country?: string;
  currentSelectedId?: string;
  isOpen: boolean;
  lineItemName?: string;
  mode?: 'steps';
  onClose: () => void;
  userId: string;
};

type State = {
  brandFilter: string[];
  cartographyFilters: AdminBoundariesItemInput[];
  metricFilters: MetricFilters;
  metricsUnitMode: MetricsUnitMode;
  metricSort: MetricSort;
  searchText: string;
  selectedPois: PoiWithUplift[];
  selectedReportType: ReportType | null;
  showCartographyModal: boolean;
  showNoUpliftPrompt: boolean;
  userHasInteracted: boolean;
  viewMode: 'grid' | 'list';
};

const SKELETON_COUNT = 10;
const MAX_POI_SELECTION_AMOUNT = 1000;

const INITIAL_STATE: State = {
  brandFilter: [],
  cartographyFilters: [],
  metricFilters: {},
  metricSort: DEFAULT_METRIC_SORT,
  metricsUnitMode: 'percentage',
  searchText: '',
  selectedPois: [],
  selectedReportType: null,
  showCartographyModal: false,
  showNoUpliftPrompt: false,
  userHasInteracted: false,
  viewMode: 'grid'
};

function formatUpliftPercent(value: number): string {
  const formatted = formatPercentage(value, { base: 100, fixedDecimals: 2 });
  return value > 0 ? `${formatted}` : formatted;
}

function adminBoundariesToTagFilters(items: AdminBoundariesItemInput[]): Record<string, string[]> {
  return items.reduce(
    (acc, item) => ({ ...acc, [item.shape]: [...(acc[item.shape] ?? []), item.name] }),
    {} as Record<string, string[]>
  );
}

const METRIC_BOUNDS_DECIMAL_DIGITS = 2;

function computeMetricBoundsFromPois(
  pois: PoiWithUplift[],
  metricsUnitMode: MetricsUnitMode
): Record<MetricKey, MetricBounds> {
  const precision = 10 ** METRIC_BOUNDS_DECIMAL_DIGITS;

  const boundsFor = (values: (number | null)[]): MetricBounds => {
    const numericValues = values.filter((value): value is number => value != null);
    if (numericValues.length === 0) {
      return { max: 0, min: 0 };
    }
    return {
      max: Math.round(Math.max(...numericValues) * precision) / precision,
      min: Math.round(Math.min(...numericValues) * precision) / precision
    };
  };

  return {
    baseline: boundsFor(pois.map(poi => poi.baseline)),
    cpsv: boundsFor(pois.map(poi => poi.costPerVisit)),
    uplift: boundsFor(pois.map(poi => (metricsUnitMode === 'percentage' ? poi.upliftPercentage : poi.uplift)))
  };
}

function sortPoisByMetric(pois: PoiWithUplift[], metricSort: MetricSort): PoiWithUplift[] {
  if (!metricSort) {
    return pois;
  }
  const { field, order } = metricSort;
  const direction = order === 'asc' ? 1 : -1;
  const getValue = (poi: PoiWithUplift): number | null => {
    switch (field) {
      case 'baseline':
        return poi.baseline;
      case 'cpsv':
        return poi.costPerVisit;
      default:
        return poi.uplift;
    }
  };
  return [...pois].sort((a, b) => {
    const valueA = getValue(a);
    const valueB = getValue(b);
    if (valueA === null && valueB === null) {
      return 0;
    }
    if (valueA === null) {
      return 1;
    }
    if (valueB === null) {
      return -1;
    }
    return direction * (valueA - valueB);
  });
}

const REPORT_TYPE_CARDS = [
  {
    description: 'driveToStore.poiSelectionModal.singleReportDescription',
    descriptionDefault: 'Select an individual POI to dive deep into its specific metrics and performance insights.',
    icon: 'fa-duotone fa-solid fa-store',
    tag: 'driveToStore.poiSelectionModal.singleReportTag',
    tagDefault: 'Drive to Store Report',
    title: 'driveToStore.poiSelectionModal.singleReportTitle',
    titleDefault: 'Single Report',
    value: 'single' as ReportType
  },
  {
    description: 'driveToStore.poiSelectionModal.aggregatedReportDescription',
    descriptionDefault: 'Get a holistic view by analyzing the combined performance of all POIs.',
    icon: 'fa-duotone fa-solid fa-store',
    tag: 'driveToStore.poiSelectionModal.aggregatedReportTag',
    tagDefault: 'All Aggregated POIs',
    title: 'driveToStore.poiSelectionModal.aggregatedReportTitle',
    titleDefault: 'Aggregated Report',
    value: 'aggregated' as ReportType
  }
] as const;

function ReportTypeStep({
  selectedType,
  onSelect
}: {
  selectedType: ReportType | null;
  onSelect: (type: ReportType) => void;
}) {
  const { formatMessage } = useIntl();

  return (
    <div className="poiSelectionModal__typeGrid">
      {REPORT_TYPE_CARDS.map(card => {
        const isSelected = selectedType === card.value;
        return (
          <Card
            className="poiSelectionModal__typeCard"
            isSelected={isSelected}
            key={card.value}
            onClick={() => onSelect(card.value)}
          >
            <Card.Header align="right" className="poiSelectionModal__typeCardHeader">
              <Tag
                className={cn('poiSelectionModal__typeCardTag', {
                  'poiSelectionModal__typeCardTag--selected': isSelected
                })}
                id={`${card.value}-type-tag`}
                status="information"
              >
                {formatMessage({ defaultMessage: card.tagDefault, id: card.tag })}
              </Tag>
            </Card.Header>
            {card.value === 'aggregated' ? (
              <div
                className={cn('poiSelectionModal__typeCardIconGrid', {
                  'poiSelectionModal__typeCardIconGrid--selected': isSelected
                })}
              >
                {Array.from({ length: 9 }).map((_, i) => (
                  <i className="fa-duotone fa-solid fa-store" key={i} />
                ))}
              </div>
            ) : (
              <Card.Media
                className={cn('poiSelectionModal__typeCardIcon', {
                  'poiSelectionModal__typeCardIcon--selected': isSelected
                })}
                icon={card.icon}
              />
            )}
            <Card.Body align="center">
              <Card.Label variant="title">
                {formatMessage({ defaultMessage: card.titleDefault, id: card.title })}
              </Card.Label>
              <Card.Label className="poiSelectionModal__typeCardDescription" ellipsis={false} variant="subtitle">
                {formatMessage({ defaultMessage: card.descriptionDefault, id: card.description })}
              </Card.Label>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
}

type PoiCardListContentProps = {
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  isSingleMode: boolean;
  selectedIdSet: Set<string>;
  onCardClick: (id: PoiWithUplift['venueId']) => void;
};

function PoiCardListContent({ formatMessage, isSingleMode: _, selectedIdSet, onCardClick }: PoiCardListContentProps) {
  return (
    <TaptapList.CardList<PoiWithUplift>
      emptyStateDescription={formatMessage({
        defaultMessage:
          "We couldn't find any POIs that match your search. Try checking the spelling or using more general keywords.",
        id: 'driveToStore.poiSelectionModal.emptyStateDescription'
      })}
      emptyStateIcon="fal fa-empty-set"
      emptyStateTitle={formatMessage({
        defaultMessage: 'No matches found',
        id: 'driveToStore.poiSelectionModal.emptyStateTitle'
      })}
    >
      {listItems =>
        listItems.map(poi => (
          <Card
            className="poiSelectionModal__card"
            isSelected={selectedIdSet.has(poi.venueId as string)}
            key={`poi-${poi.venueId}`}
            onClick={() => onCardClick(poi.venueId)}
          >
            <Card.Header align="space-between" className="poiSelectionModal__cardHeader">
              {poi.upliftPercentage !== null && (
                <Card.Label className="poiSelectionModal__upliftTag" variant="category">
                  {`${formatUpliftPercent(poi.upliftPercentage)} uplift`}
                </Card.Label>
              )}
              <Card.Label title={poi.family} variant="category">
                {poi.family}
              </Card.Label>
            </Card.Header>
            <Card.Media
              alt={poi.brand}
              className="poiSelectionModal__cardMedia"
              src={poi.imageUrl ?? getPoiImageUrl(poi.id, poi.tags?.level0?.[0])}
            />
            <Card.Body align="left">
              <Card.Label title={poi.name} variant="title">
                {poi.name}
              </Card.Label>
              <Card.Label
                className="poiSelectionModal__cardAddress"
                ellipsis={false}
                title={poi.address}
                variant="subtitle"
              >
                {poi.address}
              </Card.Label>
            </Card.Body>
          </Card>
        ))
      }
    </TaptapList.CardList>
  );
}

export default function PoiSelectionModal({
  entityType,
  entityId,
  entityCountries,
  country,
  currentSelectedId,
  isOpen,
  lineItemName,
  mode,
  onClose,
  userId
}: Props) {
  const modalRef = useRef<ModalHandler>(null);
  const [
    {
      brandFilter,
      cartographyFilters,
      metricFilters,
      metricsUnitMode,
      metricSort,
      searchText,
      selectedPois,
      selectedReportType,
      showCartographyModal,
      showNoUpliftPrompt,
      userHasInteracted,
      viewMode
    },
    setState
  ] = useState<State>(INITIAL_STATE);

  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);

  const cartographyTagFilters = useMemo(() => adminBoundariesToTagFilters(cartographyFilters), [cartographyFilters]);

  const { data: sortedData = [], isLoading } = useQueryFetch<PoiWithUplift[]>(
    [
      'footfall',
      'poi-list-with-uplift',
      entityType,
      entityId,
      country,
      cartographyTagFilters,
      brandFilter,
      metricFilters,
      metricsUnitMode
    ],
    async () =>
      getPoiListWithUplift(entityType, entityId, userId, {
        brandFilter,
        country,
        metricFilters,
        metricsUnitMode,
        tagFilters: cartographyTagFilters
      }),
    { enabled: isOpen }
  );

  const { data: unfilteredPois = [] } = useQueryFetch<PoiWithUplift[]>(
    ['footfall', 'poi-list-with-uplift-facets', entityType, entityId, country, cartographyTagFilters],
    async () => getPoiListWithUplift(entityType, entityId, userId, { country, tagFilters: cartographyTagFilters }),
    { enabled: isOpen && viewMode === 'list' }
  );

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.openModal();
      setState(INITIAL_STATE);
    } else {
      modalRef.current?.closeModal();
    }
  }, [isOpen, currentSelectedId]);

  const defaultSelectedItems = useMemo(() => {
    if (!sortedData.length) {
      return [];
    }
    if (currentSelectedId) {
      return sortedData.filter(poi => poi.id === currentSelectedId);
    }
    return sortedData.filter(poi => (poi.upliftPercentage ?? 0) > 0);
  }, [sortedData, currentSelectedId]);

  const effectiveSelectedPois = userHasInteracted ? selectedPois : defaultSelectedItems;

  const selectedIdSet = useMemo(
    () => new Set(effectiveSelectedPois.map(p => p.venueId)),
    [effectiveSelectedPois]
  ) as Set<string>;

  const brandOptions: BrandOption[] = useMemo(() => {
    const counts = new Map<string, number>();
    unfilteredPois.forEach(poi => {
      if (poi.brand) {
        counts.set(poi.brand, (counts.get(poi.brand) ?? 0) + 1);
      }
    });
    return Array.from(counts.entries()).map(([brand, count]) => ({ brand, count }));
  }, [unfilteredPois]);

  const metricBounds = useMemo(
    () => computeMetricBoundsFromPois(unfilteredPois, metricsUnitMode),
    [unfilteredPois, metricsUnitMode]
  );

  const filteredPois = sortedData.filter(poi => {
    if (!searchText) {
      return true;
    }
    const query = searchText.toLowerCase();
    return (
      poi.name.toLowerCase().includes(query) ||
      poi.address.toLowerCase().includes(query) ||
      (poi.brand?.toLowerCase().includes(query) ?? false)
    );
  });

  const displayedPois = sortPoisByMetric(filteredPois, metricSort);

  const allDisplayedSelected =
    displayedPois.length > 0 && displayedPois.every(p => selectedIdSet.has(p.venueId as string));

  const isSingleMode = mode === 'steps' && selectedReportType === 'single';
  const selectionCount = effectiveSelectedPois.length;
  const noUpliftCount = effectiveSelectedPois.filter(
    p => p.upliftPercentage === null || p.upliftPercentage <= 0
  ).length;

  const handleCartographyFilterChange = (zoneList: AdminBoundariesChangeItem[]) => {
    const nextCartographyFilters = zoneList.reduce<AdminBoundariesItemInput[]>(
      (acc, item) => [
        ...acc,
        ...item.list.map(
          (el: { description: string; name: string }): AdminBoundariesItemInput => ({
            country: item.country,
            description: el.description,
            name: el.name,
            shape: item.shape
          })
        )
      ],
      []
    );
    setState(prev => ({
      ...prev,
      cartographyFilters: nextCartographyFilters,
      selectedPois: prev.userHasInteracted ? prev.selectedPois : effectiveSelectedPois,
      showCartographyModal: false,
      userHasInteracted: true
    }));
  };

  const handleMetricFiltersChange = (nextMetricFilters: MetricFilters) => {
    setState(prev => ({ ...prev, metricFilters: nextMetricFilters }));
  };

  const handleUnitModeChange = (nextUnitMode: MetricsUnitMode) => {
    setState(prev => ({ ...prev, metricsUnitMode: nextUnitMode }));
  };

  const handleBrandFilterChange = (nextBrandFilter: string[]) => {
    setState(prev => ({ ...prev, brandFilter: nextBrandFilter }));
  };

  const handleMetricSortChange = (nextMetricSort: MetricSort) => {
    setState(prev => ({ ...prev, metricSort: nextMetricSort }));
  };

  const handleSearchTextChange = debounce((value: string) => {
    setState(prevState => ({ ...prevState, searchText: value }));
  }, 500);

  const handleSelectClick = (venueId: PoiWithUplift['venueId']) => {
    const isSelected = selectedIdSet.has(venueId as string);
    if (isSelected) {
      const next = effectiveSelectedPois.filter(p => p.venueId !== venueId);
      setState(prev => ({ ...prev, selectedPois: next, userHasInteracted: true }));
      return;
    }
    const poi = sortedData.find(p => p.venueId === venueId);
    if (!poi) {
      return;
    }
    const next = isSingleMode ? [poi] : [...effectiveSelectedPois, poi];
    setState(prev => ({ ...prev, selectedPois: next, userHasInteracted: true }));
  };

  const handleSelectAll = () => {
    if (allDisplayedSelected) {
      setState(prev => ({ ...prev, selectedPois: [], userHasInteracted: true }));
    } else {
      const displayedIdSet = new Set(displayedPois.map(p => p.venueId));
      const othersAlreadySelected = effectiveSelectedPois.filter(p => !displayedIdSet.has(p.venueId));
      setState(prev => ({
        ...prev,
        selectedPois: [...othersAlreadySelected, ...displayedPois],
        userHasInteracted: true
      }));
    }
  };

  const handlePoiClear = () => {
    setState(prev => ({ ...prev, selectedPois: [], userHasInteracted: true }));
  };

  const noUpliftPromptId = 'poiSelectionModal_noUpliftPrompt';
  const noUpliftPortalTarget = usePortal(noUpliftPromptId);

  const navigateAggregated = (pois: PoiWithUplift[]) => {
    if (pois.length > MAX_POI_SELECTION_AMOUNT) {
      navigate(getDriveToStoreReportUrl(entityType, entityId));
      onClose();
      return;
    }
    const venueIds = pois.map(p => p.venueId).filter((id): id is string => Boolean(id));
    navigate(getDriveToStoreReportUrl(entityType, entityId, { venueIds }));
    onClose();
  };

  const handleSeeSingleReport = () => {
    window.open(
      getDriveToStoreReportUrl(entityType, entityId, { poiId: effectiveSelectedPois[0]?.id }),
      '_blank',
      'noopener,noreferrer'
    );
    onClose();
  };

  const handleSeeAggregatedReport = () => {
    if (noUpliftCount > 0) {
      setState(prev => ({ ...prev, showNoUpliftPrompt: true }));
      return;
    }
    navigateAggregated(effectiveSelectedPois);
  };

  const handleConfirmNoUplift = (discardNoUplift: boolean) => {
    setState(prev => ({ ...prev, showNoUpliftPrompt: false }));
    const pois = discardNoUplift
      ? effectiveSelectedPois.filter(p => p.upliftPercentage !== null && p.upliftPercentage > 0)
      : effectiveSelectedPois;
    navigateAggregated(pois);
  };

  const noUpliftPromptPortal =
    showNoUpliftPrompt && noUpliftPortalTarget
      ? ReactDOM.createPortal(
          <CustomThemeProvider themeContrast="light">
            <NoUpliftConfirmPrompt
              noUpliftCount={noUpliftCount}
              onCancel={() => setState(prev => ({ ...prev, showNoUpliftPrompt: false }))}
              onConfirm={handleConfirmNoUplift}
              selectedCount={selectionCount}
            />
          </CustomThemeProvider>,
          noUpliftPortalTarget
        )
      : null;

  const handleShowCartographyModal = (show: boolean) => {
    setState(prev => ({ ...prev, showCartographyModal: show }));
  };

  const handleCartographyReset = () => {
    setState(prev => ({ ...prev, cartographyFilters: [], userHasInteracted: true }));
  };

  const capitalizedEntityCountries = entityCountries.map(c => c.toLocaleUpperCase());

  const cartographyModalPortal = showCartographyModal ? (
    <CartographySelectorModal
      id="poiSelectionModal-cartographySelectorModal"
      initialCountry={capitalizedEntityCountries[0]}
      initialItems={cartographyFilters}
      isModalOpened
      limitedCountries={capitalizedEntityCountries}
      minSelection={0}
      onClose={() => handleShowCartographyModal(false)}
      onSave={handleCartographyFilterChange}
      oneBoundaryType
      sonataLICountries={false}
      theme="light"
      user={user.toJSON()}
      userRoles={userRoles}
    />
  ) : null;

  const ListToolbar = () => {
    return (
      <div className="poiSelectionModal__resultsToolbar">
        <div className="poiSelectionModal__listToolbarLeft">
          <SelectionToolbar
            allSelected={allDisplayedSelected}
            hasSelection={selectionCount > 0}
            onClear={handlePoiClear}
            onSelectAll={handleSelectAll}
            resultsCount={filteredPois.length}
            selectedCount={selectionCount}
            showClear={false}
            showSelectAll={false}
          />
        </div>
        <div className="poiSelectionModal__listToolbarRight">
          {!isSingleMode && (
            <>
              <Checkbox
                active={allDisplayedSelected}
                id="poi-select-all"
                labelText={
                  allDisplayedSelected
                    ? formatMessage({ defaultMessage: 'Unselect all', id: 'common.unselectAll' })
                    : formatMessage({ defaultMessage: 'Select all', id: 'common.selectAll' })
                }
                onClickAction={handleSelectAll}
                tiny
                type="checkbox"
              />
              <ButtonUI
                disabled={selectionCount === 0}
                icon="fa-regular fa-trash"
                id="poi-clear-selection"
                onClick={handlePoiClear}
                shape="link"
                size="ultraSmall"
                text={formatMessage({
                  defaultMessage: 'Clear selection',
                  id: 'driveToStore.selectionToolbar.clearSelection'
                })}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const poiGridContent = (
    <>
      {lineItemName && (
        <div className="poiSelectionModal__header">
          <p className="poiSelectionModal__headerTitle">
            {formatMessage({ defaultMessage: 'Line Item:', id: 'driveToStore.poiSelectionModal.header' })}
          </p>
          <p className="poiSelectionModal__headerSubtitle" title={lineItemName}>
            {lineItemName}
          </p>
        </div>
      )}
      <div className="poiSelectionModal__mainSection">
        <div className="poiSelectionModal__leftSection">
          <div className="poiSelectionModal__leftSectionToolbar">
            <InputSearch
              id="poiSelectionModal__inputSearch"
              lens
              name="poiSelectionModal__inputSearch"
              onChange={handleSearchTextChange}
              placeholder={formatMessage({
                defaultMessage: 'Search for a POI by brand, address...',
                id: 'driveToStore.poiSelectionModal.searchPlaceholder'
              })}
              tiny
            />
            {viewMode === 'grid' && (
              <PopoverMenuButton
                badge={!isEqual(metricSort, DEFAULT_METRIC_SORT)}
                behavior="static"
                icon="fa-regular fa-sliders"
                id="poiSelectionModal-metricSortToggle"
                shape="squareRounded"
                size="ultraSmall"
                styles={{
                  backgroundColor: 'var(--color-sonata-secondary-white)',
                  border: '1px solid var(--color-sonata-primary-500)',
                  borderRadius: '8px',
                  padding: 'var(--spacing-12)',
                  zIndex: 110000
                }}
                title={formatMessage({
                  defaultMessage: 'Sort',
                  id: 'driveToStore.poiSelectionModal.metricSortToggle'
                })}
                type="secondary"
              >
                <CustomThemeProvider themeContrast="light">
                  <MetricSortPanel metricSort={metricSort} onChange={handleMetricSortChange} />
                </CustomThemeProvider>
              </PopoverMenuButton>
            )}
            <ButtonUI
              badge={cartographyFilters.length > 0}
              icon="fa-duotone fa-regular fa-map-location-dot"
              id="poiSelectionModal-cartographyFilterButton"
              onClick={() => handleShowCartographyModal(true)}
              shape="squareRounded"
              size="ultraSmall"
              title={formatMessage({
                defaultMessage: 'Map filter',
                id: 'driveToStore.poiSelectionModal.cartographyFilterButton'
              })}
              type="secondary"
            />
            {cartographyFilters.length > 0 && (
              <ButtonUI
                icon="fa-solid fa-arrows-rotate"
                id="poiSelectionModal-cartographyResetButton"
                onClick={handleCartographyReset}
                shape="squareRounded"
                size="ultraSmall"
                title={formatMessage({
                  defaultMessage: 'Reset filters',
                  id: 'driveToStore.poiSelectionModal.cartographyResetButton'
                })}
                type="tertiary"
              />
            )}
            <ViewToggle
              onChange={value => setState(prev => ({ ...prev, viewMode: value }))}
              options={[
                {
                  icon: 'fa-solid fa-grid-2',
                  label: formatMessage({ defaultMessage: 'Gallery', id: 'driveToStore.poiSelectionModal.viewGrid' }),
                  value: 'grid' as const
                },
                {
                  icon: 'fa-solid fa-list',
                  label: formatMessage({ defaultMessage: 'List', id: 'driveToStore.poiSelectionModal.viewList' }),
                  value: 'list' as const
                }
              ]}
              value={viewMode}
              variant="interaction"
            />
          </div>
          <ListToolbar />
          <div className="poiSelectionModal__poiContainer">
            <Switch>
              <Switch.Case condition={isLoading && viewMode === 'grid'}>
                <div className="poiSelectionModal__grid">
                  {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                    <LoadingSkeleton height={217} key={i} />
                  ))}
                </div>
              </Switch.Case>
              <Switch.Default>
                <div className="poiSelectionModal__contentRow">
                  {viewMode === 'list' && (
                    <PoiFiltersSidebar
                      brandFilter={brandFilter}
                      brandOptions={brandOptions}
                      metricBounds={metricBounds}
                      metricFilters={metricFilters}
                      onBrandFilterChange={handleBrandFilterChange}
                      onMetricFiltersChange={handleMetricFiltersChange}
                      onUnitModeChange={handleUnitModeChange}
                      unitMode={metricsUnitMode}
                    />
                  )}
                  <div className="poiSelectionModal__mainContent">
                    {viewMode === 'list' ? (
                      <PoiStoreTable
                        fetchParams={{
                          brandFilter,
                          country,
                          entityId,
                          entityType,
                          metricFilters,
                          metricsUnitMode,
                          nameFilter: searchText,
                          tagFilters: cartographyTagFilters,
                          userId
                        }}
                        height={450}
                        id="poiSelectionModal-list"
                        initialPageSize={20}
                        onToggle={handleSelectClick}
                        selectedIds={selectedIdSet}
                        showBestDay={false}
                      />
                    ) : (
                      <TaptapList<PoiWithUplift>
                        className="poiSelectionModal"
                        data={displayedPois}
                        id="poi-selection-modal"
                        key={isOpen ? 'open' : 'closed'}
                      >
                        <PoiCardListContent
                          formatMessage={formatMessage}
                          isSingleMode={isSingleMode}
                          onCardClick={handleSelectClick}
                          selectedIdSet={selectedIdSet}
                        />
                      </TaptapList>
                    )}
                  </div>
                </div>
              </Switch.Default>
            </Switch>
          </div>
        </div>
        <div className="poiSelectionModal__selectionContainer">
          <SelectionSummaryPanel
            onClearAll={handlePoiClear}
            onRemove={handleSelectClick}
            selectedPois={effectiveSelectedPois}
          />
        </div>
      </div>
    </>
  );

  if (mode === 'steps') {
    const steps: StepEntry[] = [
      {
        component: (
          <ReportTypeStep
            onSelect={type =>
              setState(prev => ({
                ...prev,
                selectedPois: [],
                selectedReportType: type,
                userHasInteracted: type === 'single'
              }))
            }
            selectedType={selectedReportType}
          />
        ),
        description: formatMessage({
          defaultMessage: 'Select Report type',
          id: 'driveToStore.poiSelectionModal.steps.selectReportType'
        }),
        title: formatMessage({
          defaultMessage: 'All reports',
          id: 'driveToStore.poiSelectionModal.allReportsTitle'
        })
      },
      {
        component: poiGridContent,
        description:
          selectedReportType === 'single'
            ? formatMessage({
                defaultMessage: 'Select 1 POI to see its report',
                id: 'driveToStore.poiSelectionModal.steps.selectPoisSingle'
              })
            : formatMessage({
                defaultMessage:
                  'These are all the POIs from your selected Line Item. Continue to see aggregated report.',
                id: 'driveToStore.poiSelectionModal.steps.selectPois'
              }),
        title: formatMessage({
          defaultMessage: 'All reports',
          id: 'driveToStore.poiSelectionModal.allReportsTitle'
        })
      }
    ];

    const finishButton =
      selectedReportType === 'single'
        ? {
            disabled: selectionCount !== 1,
            id: 'finish-see-single-report',
            onClick: handleSeeSingleReport,
            text: formatMessage({
              defaultMessage: 'See single report',
              id: 'driveToStore.poiSelectionModal.seeSingleReport'
            })
          }
        : {
            disabled: selectionCount < 1,
            id: 'finish-see-aggregated-report',
            onClick: handleSeeAggregatedReport,
            text: formatMessage({
              defaultMessage: 'See aggregated report',
              id: 'driveToStore.poiSelectionModal.seeAggregatedReport'
            })
          };

    return (
      <>
        <ModalUI
          closeModalCallback={onClose}
          disabledNextButton={selectedReportType === null}
          finishButton={finishButton}
          key={isOpen ? 'open' : 'closed'}
          leftButton={
            <ButtonUI
              id={'cancel-button'}
              onClick={onClose}
              text={formatMessage({ defaultMessage: 'Cancel', id: 'common.cancel' })}
              type="secondary"
            />
          }
          ref={modalRef}
          steps={steps}
          title=""
          width="1252px"
        />
        {noUpliftPromptPortal}
        {cartographyModalPortal}
      </>
    );
  }

  return (
    <>
      <ModalUI
        closeModalCallback={onClose}
        description={formatMessage({
          defaultMessage: 'Here are the POIs for the selected campaign. Choose one to view its detailed report.',
          id: 'driveToStore.poiSelectionModal.description'
        })}
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'Footfall report',
          id: 'driveToStore.poiSelectionModal.title'
        })}
        width="1252px"
      >
        {poiGridContent}
        <div className="poiSelectionModal__footer">
          <div className="poiSelectionModal__footerLeftButtons">
            <ButtonUI
              id={'cancel-button'}
              onClick={onClose}
              text={formatMessage({ defaultMessage: 'Cancel', id: 'common.cancel' })}
              type="secondary"
            />
          </div>
          <div className="poiSelectionModal__footerRightButtons">
            <ButtonUI
              disabled={selectionCount !== 1}
              id={'see-single-report-button'}
              onClick={handleSeeSingleReport}
              text={formatMessage({
                defaultMessage: 'See single report',
                id: 'driveToStore.poiSelectionModal.seeSingleReport'
              })}
              type="secondary"
            />
            <ButtonUI
              disabled={selectionCount < 2}
              id={'see-aggregated-report-button'}
              onClick={handleSeeAggregatedReport}
              text={formatMessage({
                defaultMessage: 'See aggregated report',
                id: 'driveToStore.poiSelectionModal.seeAggregatedReport'
              })}
              type="primary"
            />
          </div>
        </div>
      </ModalUI>
      {noUpliftPromptPortal}
      {cartographyModalPortal}
    </>
  );
}
